import {
  createTileLayerComponent,
  updateGridLayer,
  createElementObject,
  withPane,
  type LeafletContextInterface,
} from '@react-leaflet/core';
import L from 'leaflet';
import type { TileLayerProps } from 'react-leaflet';
import { getTile as defaultGetTile } from '../../services/tileCache';
import { getTileWeightSync } from '../../services/regionUsageService';

type GetTileFn = (url: string, opts?: { signal?: AbortSignal; pin?: boolean; weight?: number }) => Promise<Response>;

/**
 * Fyller et <img> med en flis via cachen. Eksportert (og med injiserbar
 * getTile) for å kunne testes uten Leaflet-internals.
 *
 * Rekkefølge:
 *   1. Hent via `getTile` (cache-first) → blob → object-URL → img.src.
 *   2. img.onload → ferdig; img.onerror (korrupt blob) → feil.
 *   3. Kaster getTile (f.eks. Cache API utilgjengelig) → fall tilbake til å
 *      sette img.src direkte til nett-URL-en, så kartet aldri knekker.
 */
export function loadCachedTile(
  img: HTMLImageElement,
  url: string,
  done: (err?: Error, tile?: HTMLElement) => void,
  getTileFn: GetTileFn = defaultGetTile,
  weight?: number,
): HTMLImageElement {
  let settled = false;
  const finish = (err?: Error) => {
    if (settled) return;
    settled = true;
    done(err, img);
  };

  (weight ? getTileFn(url, { weight }) : getTileFn(url))
    .then((res) => res.blob())
    .then((blob) => {
      const objUrl = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(objUrl);
        finish();
      };
      img.onerror = () => {
        URL.revokeObjectURL(objUrl);
        finish(new Error('tile image decode error'));
      };
      img.src = objUrl;
    })
    .catch(() => {
      // Fallback: last direkte fra nett.
      img.onload = () => finish();
      img.onerror = () => finish(new Error('tile network error'));
      img.src = url;
    });

  return img;
}

/**
 * Leaflet-flislag som går via den plattform-uavhengige flis-cachen.
 *
 * Standard L.TileLayer setter <img>.src direkte til nett-URL-en, som på iOS
 * WKWebView aldri fanges av service workeren → ingen ambient-caching. Her
 * henter vi hver flis gjennom `getTile` (cache-first, LRU, persist).
 *
 * Kun for raster-XYZ-baselag (satellitt, topo, hillshade). WMS-overlegg er
 * dynamiske per-viewport og rutes ikke hit.
 */
class CachedLeafletTileLayer extends L.TileLayer {
  createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const img = document.createElement('img');
    img.setAttribute('role', 'presentation');
    img.alt = '';

    const crossOrigin = this.options.crossOrigin;
    if (crossOrigin || crossOrigin === '') {
      img.crossOrigin = crossOrigin === true ? '' : (crossOrigin as string);
    }
    const referrerPolicy = (this.options as { referrerPolicy?: string }).referrerPolicy;
    if (referrerPolicy) img.referrerPolicy = referrerPolicy;

    // Hotness for flisens celle → beskytter fliser i mye-brukte områder mot eviction.
    const weight = getTileWeightSync(coords.x, coords.y, coords.z);
    return loadCachedTile(img, this.getTileUrl(coords), done, defaultGetTile, weight);
  }
}

export const CachedTileLayer = createTileLayerComponent<CachedLeafletTileLayer, TileLayerProps>(
  function createCachedTileLayer({ url, ...options }, context: LeafletContextInterface) {
    const layer = new CachedLeafletTileLayer(url, withPane(options, context));
    return createElementObject(layer, context);
  },
  function updateCachedTileLayer(layer, props, prevProps) {
    updateGridLayer(layer, props, prevProps);
    if (props.url != null && props.url !== prevProps.url) {
      layer.setUrl(props.url);
    }
  },
);

export default CachedTileLayer;
